// +----------------------------------------------------------------------
// | 偏好设置：AI Memory 开关 / 快速模式追问上限 / AI 语气
// +----------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSettings, updateSettings } from '../../api/user';
import { toast } from '../../components/Toast';
import useAuth from '../../hooks/useAuth';
import './Settings.less';

const TONE_OPTIONS = [
  { value: 'friendly', label: '温和一点', desc: '说得客气些，多给点鼓励' },
  { value: 'direct', label: '直说就好', desc: '别绕弯子，直接讲重点' },
];

const QUESTION_OPTIONS = [1, 2, 3, 4, 5];

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    memory_enabled: 1,
    quick_max_questions: 3,
    ai_tone: 'friendly',
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getSettings();
        if (!mounted) return;
        setSettings({
          memory_enabled: data.memory_enabled === undefined ? 1 : Number(data.memory_enabled),
          quick_max_questions: Number(data.quick_max_questions || 3),
          ai_tone: data.ai_tone || 'friendly',
        });
      } catch (e) {
        if (mounted) toast(e.message || '设置没拉到', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  /** 改动即保存（设置项少，不做「保存」按钮更轻） */
  const save = async (partial) => {
    const next = { ...settings, ...partial };
    setSettings(next);
    setSaving(true);
    try {
      await updateSettings(partial);
      toast('已保存', 'success');
    } catch (e) {
      setSettings(settings); // 失败回滚
      toast(e.message || '没保存上', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return <div className="page center muted">正在加载…</div>;
  }

  return (
    <div className="page settings">
      <h1 className="settings-title">偏好设置</h1>

      <div className="card">
        <div className="settings-row">
          <div className="settings-row-main">
            <strong>让 AI 记住我</strong>
            <span className="muted">
              记住你的偏好和习惯（比如「比较看重性价比」），下次分析时用得上。随时可以关掉，也可以删掉已记的内容。
            </span>
          </div>
          <button
            type="button"
            className={`switch ${Number(settings.memory_enabled) === 1 ? 'is-on' : ''}`}
            onClick={() =>
              save({ memory_enabled: Number(settings.memory_enabled) === 1 ? 0 : 1 })
            }
            disabled={saving}
            aria-label="记忆开关"
          >
            <span className="switch-knob" />
          </button>
        </div>
      </div>

      <div className="card">
        <div className="settings-block">
          <strong>⚡ 30 秒决定，最多问几个问题</strong>
          <span className="muted">问得越少越快，但可能不够准。</span>
          <div className="settings-options">
            {QUESTION_OPTIONS.map((num) => (
              <button
                key={num}
                type="button"
                className={`settings-option ${Number(settings.quick_max_questions) === num ? 'is-active' : ''}`}
                onClick={() => save({ quick_max_questions: num })}
                disabled={saving}
              >
                {num} 个
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="settings-block">
          <strong>AI 说话的语气</strong>
          <span className="muted">影响它给你的分析怎么表达，不影响判断。</span>
          <div className="settings-tone">
            {TONE_OPTIONS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`settings-tone-item ${settings.ai_tone === item.value ? 'is-active' : ''}`}
                onClick={() => save({ ai_tone: item.value })}
                disabled={saving}
              >
                <strong>{item.label}</strong>
                <span className="muted">{item.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card settings-danger">
        <div className="settings-row">
          <div className="settings-row-main">
            <strong>退出登录</strong>
            <span className="muted">纠结的记录会留在账号里，下次登录还在。</span>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleLogout}>
            退出
          </button>
        </div>
      </div>
    </div>
  );
}
